// Sleep quiz — ORIGINAL wording (not derived from PSQI/Epworth/ISI, which are
// licensed). Wellness self-check, non-diagnostic. 15 questions across 4 dimensions.
// Tally scoring → 4 sleep "types", each routed to calming/nervine herbs.
const sleep = {
  slug: "sleep",
  title: "What's disrupting your sleep?",
  emoji: "🌙",
  blurb: "A 15-question check across 4 sleep dimensions to find your pattern and the herbs that match it.",
  seoTitle: "Sleep Quiz: What's Disrupting Your Sleep? | Sarv",
  seoDescription:
    "Take the free sleep quiz to find out what's really keeping you up — a racing mind, restless nights, or feeling tired-but-wired — and which natural remedies can help.",
  keywords:
    "sleep quiz, why can't I sleep, natural sleep remedies, herbs for sleep, racing mind at night, can't stay asleep",
  intro: "15 quick questions across sleep onset, staying asleep, and daytime energy. A general wellness check, not a medical diagnosis.",
  evidenceNote:
    "This assessment is organized around the dimensions sleep researchers use — sleep onset, sleep maintenance, daytime impact, and lifestyle factors — with special attention to cognitive arousal (a 'racing mind'), the strongest driver of insomnia in the literature. The herb suggestions reflect traditional use; note that the clinical evidence for calming herbs is still mixed, as the balanced NCCIH reviews below explain. This is an educational self-reflection tool, not a clinical sleep assessment.",
  references: [
    {
      label: "Cognitive factors and processes in models of insomnia: a systematic review",
      publisher: "PMC / NIH",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC10909484/",
    },
    {
      label: "Sleep hygiene efficacy on quality of sleep among insomniac patients",
      publisher: "PMC / NIH",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC11610801/",
    },
    {
      label: "Sleep Disorders and Complementary Health Approaches: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/sleep-disorders-and-complementary-health-approaches",
    },
    {
      label: "Valerian: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/valerian",
    },
    {
      label: "Passionflower: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/passionflower",
    },
    {
      label: "Ashwagandha: Is it helpful for stress, anxiety, or sleep?",
      publisher: "NIH Office of Dietary Supplements",
      url: "https://ods.od.nih.gov/factsheets/Ashwagandha-HealthProfessional/",
    },
  ],
  scoring: { mode: "tally" },
  questions: [
    // --- Falling asleep ---
    {
      section: "Falling asleep",
      question: "When you get into bed, what usually happens?",
      options: [
        { label: "My thoughts start racing and won't switch off", scores: { racing: 1 } },
        { label: "I drift off fine, but wake later and struggle to get back", scores: { restless: 1 } },
        { label: "I'm exhausted yet feel too keyed-up to sleep", scores: { wired: 1 } },
        { label: "I fall asleep easily but wake up feeling unrested", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Falling asleep",
      question: "How long does it usually take you to fall asleep?",
      options: [
        { label: "A long time — my mind won't settle", scores: { racing: 1 } },
        { label: "Not long, but I don't stay asleep", scores: { restless: 1 } },
        { label: "It varies — some nights I'm buzzing", scores: { wired: 1 } },
        { label: "I fall asleep quickly", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Falling asleep",
      question: "What's going through your head at lights-out?",
      options: [
        { label: "A racing loop of thoughts and worries", scores: { racing: 1 } },
        { label: "Not much — I drop off, then wake later", scores: { restless: 1 } },
        { label: "I feel alert and buzzy despite being tired", scores: { wired: 1 } },
        { label: "Nothing unusual — but sleep still isn't restorative", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Falling asleep",
      question: "Which is closest to your experience at bedtime?",
      options: [
        { label: "I can't quiet my mind", scores: { racing: 1 } },
        { label: "I fall asleep, but it doesn't last", scores: { restless: 1 } },
        { label: "I'm too keyed-up to settle", scores: { wired: 1 } },
        { label: "I sleep, but wake unrefreshed", scores: { unrefreshing: 1 } },
      ],
    },
    // --- Staying asleep ---
    {
      section: "Staying asleep",
      question: "What's most true about your nights?",
      options: [
        { label: "My mind loops over the day or tomorrow's plans", scores: { racing: 1 } },
        { label: "I wake at the slightest noise or to use the bathroom", scores: { restless: 1 } },
        { label: "I get a 'second wind' late in the evening", scores: { wired: 1 } },
        { label: "I sleep plenty of hours but still feel tired", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Staying asleep",
      question: "If you wake in the night, what happens?",
      options: [
        { label: "My mind switches on and starts racing", scores: { racing: 1 } },
        { label: "I struggle to get back to sleep", scores: { restless: 1 } },
        { label: "I feel wired and alert", scores: { wired: 1 } },
        { label: "I fall back asleep but wake tired anyway", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Staying asleep",
      question: "What's your relationship with screens at night?",
      options: [
        { label: "I scroll to try to distract my racing thoughts", scores: { racing: 1 } },
        { label: "I check my phone when I wake in the night", scores: { restless: 1 } },
        { label: "Screens leave me feeling alert and wired", scores: { wired: 1 } },
        { label: "I use them normally and still sleep — just not deeply", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Staying asleep",
      question: "How would you describe your sleep depth?",
      options: [
        { label: "Shallow — my mind stays half-awake", scores: { racing: 1 } },
        { label: "Fragmented — I wake repeatedly", scores: { restless: 1 } },
        { label: "Restless — I feel tense even in sleep", scores: { wired: 1 } },
        { label: "Deep enough, but not refreshing", scores: { unrefreshing: 1 } },
      ],
    },
    // --- Daytime & energy ---
    {
      section: "Daytime & energy",
      question: "How would you describe your energy during the day?",
      options: [
        { label: "Anxious or mentally busy — hard to quiet down", scores: { racing: 1 } },
        { label: "Up and down — fine, then suddenly drained", scores: { restless: 1 } },
        { label: "Running on adrenaline or caffeine to keep going", scores: { wired: 1 } },
        { label: "Foggy and heavy, like I never fully woke up", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Daytime & energy",
      question: "How do you feel when you first wake up?",
      options: [
        { label: "Like I never really switched off", scores: { racing: 1 } },
        { label: "Groggy from a broken night", scores: { restless: 1 } },
        { label: "Tense or already on high alert", scores: { wired: 1 } },
        { label: "Heavy and unrefreshed despite enough sleep", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Daytime & energy",
      question: "How's your mood and focus during the day?",
      options: [
        { label: "Anxious, wired, and over-thinking", scores: { racing: 1 } },
        { label: "Variable — I crash after a bad night", scores: { restless: 1 } },
        { label: "Tense and on-edge — I can't relax", scores: { wired: 1 } },
        { label: "Foggy, heavy, and sluggish", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Daytime & energy",
      question: "How has your stress been lately?",
      options: [
        { label: "High — my head is always full", scores: { racing: 1 } },
        { label: "Moderate, and it tends to wake me at night", scores: { restless: 1 } },
        { label: "Chronic — I've been running hot for a while", scores: { wired: 1 } },
        { label: "Not especially — I'm just always tired", scores: { unrefreshing: 1 } },
      ],
    },
    // --- Habits & triggers ---
    {
      section: "Habits & triggers",
      question: "How does caffeine affect you?",
      options: [
        { label: "Even small amounts keep my mind going", scores: { racing: 1 } },
        { label: "Late caffeine wakes me during the night", scores: { restless: 1 } },
        { label: "I rely on it to push through mental fog", scores: { wired: 1 } },
        { label: "It's not really my main issue", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Habits & triggers",
      question: "Which statement fits you best?",
      options: [
        { label: "I'm a worrier, and it shows up at night", scores: { racing: 1 } },
        { label: "I'm a light sleeper", scores: { restless: 1 } },
        { label: "I'm always 'on' and can't downshift", scores: { wired: 1 } },
        { label: "I sleep fine on paper but wake tired", scores: { unrefreshing: 1 } },
      ],
    },
    {
      section: "Habits & triggers",
      question: "What would help you most right now?",
      options: [
        { label: "A way to calm a busy mind", scores: { racing: 1 } },
        { label: "Staying asleep through the night", scores: { restless: 1 } },
        { label: "Coming down from constant alertness", scores: { wired: 1 } },
        { label: "Actually waking up refreshed", scores: { unrefreshing: 1 } },
      ],
    },
  ],
  results: {
    racing: {
      name: "The Racing Mind",
      emoji: "🌀",
      tagline: "Your body is ready for sleep — your mind hasn't caught up.",
      summary:
        "Your answers point to the single most common driver of poor sleep: cognitive arousal — a mind that stays switched 'on' at exactly the moment your body is ready to rest. Your sleep machinery is likely intact; the problem is that racing thoughts, planning, and worry keep your nervous system in a state that's simply incompatible with drifting off. The most effective approach targets the mind rather than sleep itself: a genuine wind-down that separates day from night, a way to offload thoughts before bed (a 'brain dump' list works well), and calming herbs traditionally used to quiet mental chatter. Trimming afternoon caffeine and late-night screens matters more than most people expect, since both keep the brain subtly alert for hours. Small, consistent evening cues gradually teach your brain that it's finally safe to power down.",
      focus: [
        "A screen-free wind-down and a 'brain dump' journal before bed.",
        "Gentle, calming herbs traditionally used to quiet an active mind.",
        "Cutting caffeine after midday, which lingers longer than most people expect.",
      ],
      herbs: ["Passionflower", "Lemon balm", "Chamomile"],
      categorySlug: "neurological-and-cognitive-health",
    },
    restless: {
      name: "The Restless Sleeper",
      emoji: "🌘",
      tagline: "You fall asleep — staying asleep is the challenge.",
      summary:
        "Your answers point to a sleep-maintenance pattern: you can fall asleep, but you don't stay down — waking in the night and struggling to drift back off. Fragmented sleep like this often traces back to a nervous system that doesn't fully settle, an environment that's easy to wake in, or an irregular schedule that leaves your body clock unsure when it's truly night. The focus is protecting the continuity of your sleep: a cool, dark, quiet room, a consistent sleep-and-wake time (even on weekends), and herbs traditionally used to support deeper, more sustained sleep. Because alcohol and late, heavy meals both fragment sleep in the second half of the night, easing back on those can make a surprising difference. The goal isn't more hours in bed — it's fewer interruptions to the hours you already get.",
      focus: [
        "A cool, dark, quiet room to reduce night-time wake-ups.",
        "Herbs traditionally used to support deeper, more sustained sleep.",
        "A consistent sleep and wake time, even on weekends.",
      ],
      herbs: ["Valerian", "Hops", "Skullcap"],
      categorySlug: "neurological-and-cognitive-health",
    },
    wired: {
      name: "Tired but Wired",
      emoji: "⚡",
      tagline: "You're exhausted, yet somehow too alert to rest.",
      summary:
        "Your answers describe the classic 'tired-but-wired' state: exhausted on the surface, yet too activated to actually rest. This usually reflects a nervous system that's been running in high gear for a while — often from sustained stress — so your body's natural wind-down signals get overridden by a lingering sense of alertness. Because the root is daytime over-activation rather than sleep itself, the most effective approach works in both directions: lowering the overall stress load during the day, and using calming, adaptogenic herbs traditionally used to help an over-revved system downshift in the evening. Morning light and movement help re-anchor your day–night rhythm, so your body relearns when to ramp up and, just as importantly, when to let go.",
      focus: [
        "Daytime stress support so your system can wind down at night.",
        "Adaptogenic herbs traditionally used to ease a 'wired' state.",
        "Morning light and movement to reset your day-night rhythm.",
      ],
      herbs: ["Ashwagandha", "Holy basil (tulsi)", "Lavender"],
      categorySlug: "mental-and-cognitive-function",
    },
    unrefreshing: {
      name: "The Unrefreshing Sleep",
      emoji: "😴",
      tagline: "You get the hours — just not the recovery.",
      summary:
        "Your answers describe sleep that looks fine on paper — enough hours — but doesn't leave you restored. Waking foggy and heavy despite adequate time in bed usually points to a quality issue rather than a quantity one: fragmented deep sleep, an irregular schedule, or habits like late eating, alcohol, or too little daytime light that quietly erode how restorative your nights are. The goal is improving the depth and quality of your sleep and your daytime recovery — using restorative 'tonic' herbs alongside consistent sleep timing and more daylight and movement earlier in the day. If unrefreshing sleep persists despite good habits, it's worth ruling out other causes (like sleep apnea or low iron) with a professional, since the fix there is different.",
      focus: [
        "Protecting sleep quality: consistent timing, less late eating and alcohol.",
        "Restorative, tonic herbs traditionally used to support recovery.",
        "Daylight and movement earlier in the day for deeper rest at night.",
      ],
      herbs: ["Reishi", "Ashwagandha", "Chamomile"],
      categorySlug: "neurological-and-cognitive-health",
    },
  },
};

export default sleep;
